import {
  useQuery,
  useMutation,
  useQueryClient,
  UseQueryOptions,
  UseMutationOptions
} from '@tanstack/react-query';
import { ApiError } from '@/api/client';
import {
  createValidation,
  fetchValidations,
  fetchLatestValidation,
  fetchValidationsForFinding
} from './api';
import {
  ValidationDetails,
  ValidationCreateRequest,
  ValidationListParams
} from './types';

export const validationKeys = {
  all: ['validations'] as const,
  lists: () => [...validationKeys.all, 'list'] as const,
  list: (params?: ValidationListParams) =>
    [...validationKeys.lists(), params] as const,
  latestForFinding: (findingId: string) =>
    [...validationKeys.all, 'latest', findingId] as const,
  historyForFinding: (
    findingId: string,
    params?: { skip?: number; limit?: number }
  ) => [...validationKeys.all, 'history', findingId, params] as const
};

/**
 * Hook to create a new validation
 */
export function useCreateValidation(
  options?: Omit<
    UseMutationOptions<
      ValidationDetails,
      ApiError,
      {
        payload: ValidationCreateRequest;
        options?: { enforce_reason?: boolean; latest_wins?: boolean };
      }
    >,
    'mutationFn'
  >
) {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ payload, options: createOptions }) =>
      createValidation(payload, createOptions),
    onSuccess: async (data) => {
      queryClient.setQueryData(
        validationKeys.latestForFinding(data.finding_id),
        data
      );

      await queryClient.invalidateQueries({
        queryKey: validationKeys.latestForFinding(data.finding_id)
      });

      await queryClient.refetchQueries({
        queryKey: validationKeys.latestForFinding(data.finding_id),
        exact: true
      });

      queryClient.invalidateQueries({
        queryKey: validationKeys.historyForFinding(data.finding_id)
      });
      queryClient.invalidateQueries({
        queryKey: validationKeys.list({ finding_id: data.finding_id })
      });
      queryClient.invalidateQueries({
        queryKey: validationKeys.lists()
      });
    },
    ...options
  });
}

/**
 * Hook to fetch list of validations with filters
 */
export function useValidations(
  params?: ValidationListParams,
  options?: Omit<
    UseQueryOptions<ValidationDetails[], ApiError>,
    'queryKey' | 'queryFn'
  >
) {
  return useQuery({
    queryKey: validationKeys.list(params),
    queryFn: () => fetchValidations(params),
    staleTime: 2 * 60 * 1000, // 2 minutes
    ...options
  });
}

/**
 * Hook to fetch the latest validation for a finding
 */
export function useLatestValidation(
  findingId: string | null | undefined,
  options?: Omit<
    UseQueryOptions<ValidationDetails, ApiError>,
    'queryKey' | 'queryFn'
  >
) {
  return useQuery({
    queryKey: validationKeys.latestForFinding(findingId!),
    queryFn: () => fetchLatestValidation(findingId!),
    enabled: !!findingId,
    staleTime: 0,
    retry: (failureCount, error) => {
      // Don't retry on 404 (no validation found)
      if (error instanceof Error && error.message.includes('404')) {
        return false;
      }
      return failureCount < 3;
    },
    refetchOnMount: true,
    refetchOnWindowFocus: false,
    ...options
  });
}

/**
 * Hook to fetch all validations (history) for a finding
 */
export function useValidationHistory(
  findingId: string | null | undefined,
  params?: {
    skip?: number;
    limit?: number;
  },
  options?: Omit<
    UseQueryOptions<ValidationDetails[], ApiError>,
    'queryKey' | 'queryFn'
  >
) {
  return useQuery({
    queryKey: validationKeys.historyForFinding(findingId!, params),
    queryFn: () => fetchValidationsForFinding(findingId!, params),
    enabled: !!findingId,
    staleTime: 5 * 60 * 1000, // 5 minutes (history doesn't change as often)
    ...options
  });
}
